import client from './client'
import type {
  Newsletter,
  NewsletterAudience,
  RecipientCount,
  StoreNewsletterPayload,
  UpdateNewsletterPayload,
  UpsertAudiencePayload,
  SendNewsletterPayload,
  SendNewsletterSuccess,
} from '@/types/newsletter'
import type { PaginatedResponse } from '@/types/finance'

function base(organisationId: number): string {
  return `/organizer/organisations/${organisationId}/newsletters`
}

export async function listNewsletters(
  organisationId: number,
  params: { status?: string; page?: number } = {},
): Promise<PaginatedResponse<Newsletter>> {
  const { data } = await client.get<{ data: Newsletter[]; meta: PaginatedResponse<Newsletter>['meta'] }>(
    base(organisationId),
    { params },
  )
  return { data: data.data, meta: data.meta }
}

export async function storeNewsletter(
  organisationId: number,
  payload: StoreNewsletterPayload,
): Promise<Newsletter> {
  const { data } = await client.post<{ data: Newsletter }>(base(organisationId), payload)
  return data.data
}

export async function showNewsletter(
  organisationId: number,
  newsletterId: number,
): Promise<Newsletter> {
  const { data } = await client.get<{ data: Newsletter }>(
    `${base(organisationId)}/${newsletterId}`,
  )
  return data.data
}

export async function updateNewsletter(
  organisationId: number,
  newsletterId: number,
  payload: UpdateNewsletterPayload,
): Promise<Newsletter> {
  const { data } = await client.put<{ data: Newsletter }>(
    `${base(organisationId)}/${newsletterId}`,
    payload,
  )
  return data.data
}

export async function destroyNewsletter(
  organisationId: number,
  newsletterId: number,
): Promise<void> {
  await client.delete(`${base(organisationId)}/${newsletterId}`)
}

export async function upsertNewsletterAudience(
  organisationId: number,
  newsletterId: number,
  payload: UpsertAudiencePayload,
): Promise<NewsletterAudience> {
  const { data } = await client.put<{ data: NewsletterAudience }>(
    `${base(organisationId)}/${newsletterId}/audience`,
    payload,
  )
  return data.data
}

export async function fetchRecipientCount(
  organisationId: number,
  newsletterId: number,
  params?: Partial<UpsertAudiencePayload>,
): Promise<RecipientCount> {
  const { data } = await client.get<{ data: RecipientCount }>(
    `${base(organisationId)}/${newsletterId}/recipients/count`,
    { params },
  )
  return data.data
}

export async function sendTestNewsletter(
  organisationId: number,
  newsletterId: number,
): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>(
    `${base(organisationId)}/${newsletterId}/test-send`,
  )
  return data
}

export async function sendNewsletter(
  organisationId: number,
  newsletterId: number,
  payload: SendNewsletterPayload,
): Promise<SendNewsletterSuccess> {
  const { data } = await client.post<SendNewsletterSuccess>(
    `${base(organisationId)}/${newsletterId}/send`,
    payload,
  )
  return data
}

export async function cancelNewsletter(
  organisationId: number,
  newsletterId: number,
): Promise<Newsletter> {
  const { data } = await client.post<{ data: Newsletter }>(
    `${base(organisationId)}/${newsletterId}/cancel`,
  )
  return data.data
}
